import { FC, useContext, useEffect, useMemo } from 'react';
import { createGQLDataContext } from 'utils/createDataContext';
import { useGQLQuery } from 'hooks';
import { GQLAPIContextType } from 'types/common.types';
import {
  FetchTieringDetailsQuery,
  BiddingTime,
  FetchTieringDetailsQueryVariables,
} from 'types/graphql';
import { useLocation } from 'react-router-dom';
import { UseGQLQueryConfig } from 'hooks/useGQLQuery/useGQLQuery.types';
import { QUERY_shiftDistributionRulesTieringDetails } from 'queries';

type ShiftDistributionData = {
  locationName: string;
  biddingTimes: Nullable<BiddingTime[]>;
};

export const fetchShiftDistributionLocationConfig = (
  locationId: number,
): UseGQLQueryConfig<
  FetchTieringDetailsQuery,
  ShiftDistributionData,
  FetchTieringDetailsQueryVariables,
  FetchTieringDetailsQueryVariables
> => {
  return {
    key: 'SHIFT_DISTRIBUTION_RULES_MODAL_LOCATION_QUERY',
    query: QUERY_shiftDistributionRulesTieringDetails,
    variables: {
      id: locationId,
    },
    responseTransformer: (data: FetchTieringDetailsQuery): ShiftDistributionData => {
      return {
        locationName: data && data.fetchCommunity ? data.fetchCommunity.name : '',
        biddingTimes: data?.fetchCommunity?.biddingTimes?.length
          ? [...data.fetchCommunity.biddingTimes].sort((a, b) => a.priorityTime - b.priorityTime)
          : null,
      } as ShiftDistributionData;
    },
  };
};

export const ShiftDistributionLocationDataContext =
  createGQLDataContext<GQLAPIContextType<ShiftDistributionData>>();

export const ShiftDistributionLocationDataProvider: FC = ({ children }) => {
  const { pathname } = useLocation();
  const locationId = pathname.split('/')[3];

  const { data, fetch } = useGQLQuery<FetchTieringDetailsQuery, ShiftDistributionData, {}>(
    fetchShiftDistributionLocationConfig(Number.parseInt(locationId)),
  );

  useEffect(() => {
    if (locationId) {
      fetch();
    }
  }, []); // eslint-disable-line

  const contextValues = useMemo(() => {
    return {
      locationName: data?.locationName,
      biddingTimes: data?.biddingTimes,
    };
  }, [data]);

  return (
    <ShiftDistributionLocationDataContext.Provider value={contextValues}>
      {children}
    </ShiftDistributionLocationDataContext.Provider>
  );
};

export const useShiftDistributionLocationDataContext =
  (): GQLAPIContextType<ShiftDistributionData> => useContext(ShiftDistributionLocationDataContext);
